
import React from 'react';
import { GraphTeam } from '@/hooks/useGraphTeams';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck, AlertTriangle, Clock, Lock, CheckCircle, XCircle, Info } from 'lucide-react';
import { ChartContainer } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';

interface HealthMetricsCardProps {
  teams: GraphTeam[];
}

const HealthMetricsCard = ({ teams }: HealthMetricsCardProps) => {
  // Calculate governance metrics
  const totalTeams = teams.length;
  const compliantTeams = teams.filter(t => t.compliance && t.compliance >= 80).length;
  const reviewTeams = teams.filter(t => t.compliance && t.compliance >= 60 && t.compliance < 80).length;
  const atRiskTeams = teams.filter(t => t.compliance && t.compliance >= 40 && t.compliance < 60).length;
  const criticalTeams = teams.filter(t => t.compliance && t.compliance < 40).length;
  
  // Calculate average compliance score
  const avgCompliance = teams.length 
    ? Math.round(teams.reduce((acc, team) => acc + (team.compliance || 0), 0) / teams.length) 
    : 0;
  
  // Stats for governance KPIs
  const stats = [
    {
      label: 'Compliant',
      count: compliantTeams,
      total: totalTeams,
      icon: <ShieldCheck className="h-5 w-5" />,
      color: 'bg-green-500/10 text-green-500 dark:bg-green-500/20 dark:text-green-400'
    },
    {
      label: 'At Risk',
      count: atRiskTeams,
      total: totalTeams,
      icon: <AlertTriangle className="h-5 w-5" />,
      color: 'bg-amber-500/10 text-amber-500 dark:bg-amber-500/20 dark:text-amber-400'
    },
    {
      label: 'Critical',
      count: criticalTeams,
      total: totalTeams,
      icon: <XCircle className="h-5 w-5" />,
      color: 'bg-red-500/10 text-red-500 dark:bg-red-500/20 dark:text-red-400'
    }
  ];

  // Generate trend data for last 6 months with realistic variations
  const generateTrendData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    // Start with a lower score and improve over time
    return months.map((month, index) => {
      const compliantValue = Math.min(totalTeams, Math.max(0, Math.round(totalTeams * (0.5 + index * 0.07) + (Math.random() * 2 - 1))));
      const atRiskValue = Math.min(totalTeams - compliantValue, Math.max(0, Math.round(totalTeams * (0.3 - index * 0.02) + (Math.random() * 2 - 1))));
      const criticalValue = Math.max(0, totalTeams - compliantValue - atRiskValue);
      
      return {
        month,
        Compliant: compliantValue,
        'At Risk': atRiskValue,
        Critical: criticalValue
      };
    });
  };

  const trendData = generateTrendData();
  
  // Chart configs with enhanced colors
  const lineChartConfig = {
    Compliant: { color: '#10B981' },
    'At Risk': { color: '#F97316' },
    Critical: { color: '#EF4444' },
  };

  // Calculate percentages for better labels
  const getPercentage = (count: number) => {
    return totalTeams > 0 ? Math.round((count / totalTeams) * 100) : 0;
  };
  
  return (
    <Card className="bg-card dark:bg-card border border-border dark:border-slate-700/50 shadow-sm dark:shadow-md">
      <CardHeader>
        <CardTitle className="text-xl text-foreground dark:text-foreground">Governance Metrics</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats Grid */}
        <div className="grid gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="flex items-center p-3 bg-muted/50 dark:bg-slate-800/50 rounded-lg shadow-sm">
              <div className={`mr-4 rounded-full p-2 ${stat.color}`}>
                {stat.icon}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-foreground dark:text-foreground">{stat.count}</span>
                <span className="text-sm text-muted-foreground">of {stat.total}</span>
              </div>
              <span className="ml-auto text-sm">{stat.label}</span>
            </div>
          ))}
        </div>
        
        {/* Compliance Growth Visualization */}
        <div>
          <h3 className="text-lg font-medium mb-4 text-foreground dark:text-foreground">Teams Compliance Trend</h3>
          
          <div className="h-64 mt-2">
            <ChartContainer config={lineChartConfig}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={trendData}
                  margin={{ top: 5, right: 30, left: 0, bottom: 25 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted dark:stroke-slate-700" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: 'var(--foreground)' }} 
                    tickLine={{ stroke: 'var(--muted-foreground)' }}
                    axisLine={{ stroke: 'var(--muted-foreground)' }}
                  />
                  <YAxis 
                    tick={{ fill: 'var(--foreground)' }}
                    tickLine={{ stroke: 'var(--muted-foreground)' }}
                    axisLine={{ stroke: 'var(--muted-foreground)' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--card)',
                      borderColor: 'var(--border)',
                      borderRadius: '8px',
                      color: 'var(--foreground)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="Compliant" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 0, fill: "#10B981" }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="At Risk" 
                    stroke="#F97316" 
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 0, fill: "#F97316" }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Critical" 
                    stroke="#EF4444" 
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 0, fill: "#EF4444" }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HealthMetricsCard;
